// src/pages/PrescriptionTransfer.jsx

import React, { useState } from 'react';
import axios from 'axios';
import '../styles/prescriptionTransferPage.css';
import useScrollAnimation from '../hooks/useScrollAnimation';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import { formatPhoneNumber } from '../utils/phoneFormatter';
import { PHARMACY_NUMBER } from '../constants/constant';

const API_BASE = process.env.REACT_APP_API_URL;

function PrescriptionTransfer() {
    useScrollAnimation();

    const initialFormData = useState({
        pharmacyName: '',
        pharmacyPhone: '',
        transferType: 'all',
        firstName: '',
        lastName: '',
        email: '',
        birthDate: '',
        phone: '',
        consent1: false,
        consent2: false,
    });

    const [formData, setFormData] = useState(initialFormData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const resetForm = () => {
        setFormData(initialFormData);
        setSubmitStatus(null);
        
        // Force clear all input fields (including autofilled ones)
        const form = document.querySelector('.pt-form');
        if (form) {
            form.reset();
            const inputs = form.querySelectorAll('input');
            inputs.forEach(input => {
                if (input.type !== 'radio' && input.type !== 'checkbox') {
                    input.value = '';
                    input.setAttribute('autocomplete', 'off');
                }
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            await axios.post(`${API_BASE}api/send-prescription`, formData);
            // await axios.post('http://localhost:8000/api/send-prescription', formData);

            setSubmitStatus({
                type: 'success',
                message: 'Transfer request submitted successfully! We will contact you shortly to process your prescription transfer.'
            });

            // Reset form after 2 seconds
            setTimeout(() => {
                resetForm();
            }, 2000);
        } catch (err) {
            console.error(err);
            setSubmitStatus({
                type: 'error',
                message: `Failed to send the request. Please try again or call us directly at ${formatPhoneNumber(PHARMACY_NUMBER)}.`
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
        <Navbar/>
        <section className="pt-wrapper">
            {isSubmitting && (
                <div className="pt-loading-overlay">
                    <div className="pt-spinner-container">
                        <div className="pt-spinner"></div>
                        <p>Submitting your transfer request...</p>
                    </div>
                </div>
            )}
            <div className="pt-intro-section slide-down">
                <div className="pt-header fade-in">
                    <div className="pt-title-section">
                        <div className="prescription-icon">
                            <i className="fas fa-file-prescription"></i>
                        </div>
                        <h1>PRESCRIPTION TRANSFER</h1>
                    </div>
                    <p>Are you moving? Do you need to change pharmacies?</p>
                    <p>
                        If you wish to transfer your prescriptions to Mirror Lake Pharmacy, simply fill out
                        <strong> the secure form</strong> below to send a transfer request.
                    </p>
                    <p>We'll take care of the rest!</p>
                </div>

                <div className="pt-steps-section fade-in-delay">
                    <h2>3 EASY STEPS</h2>
                    <hr />
                    <div className="pt-steps">
                        <div className="pt-step step-1">
                            <i className="fas fa-file-alt fa-2x pt-icon-step"></i>
                            <p><strong>Step 1</strong><br />Fill out the form</p>
                        </div>
                        <div className="pt-step step-2">
                            <i className="fas fa-phone-alt fa-2x pt-icon-step"></i>
                            <p><strong>Step 2</strong><br />Our pharmacy will call you</p>
                        </div>
                        <div className="pt-step step-3">
                            <i className="fas fa-notes-medical fa-2x pt-icon-step"></i>
                            <p><strong>Step 3</strong><br />Your prescription is transferred!</p>
                        </div>
                    </div>
                </div>
            </div>

            {submitStatus && (
                <div className={`pt-status-message ${submitStatus.type === 'success' ? 'pt-status-success' : 'pt-status-error'}`}>
                    <i className={`fas ${submitStatus.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                    <span>{submitStatus.message}</span>
                </div>
            )}

            <div className="pt-card slide-up">
                <form className="pt-form fade-in-delay-2" onSubmit={handleSubmit}>
                    {/* TRANSFER FROM */}
                    <h3>Transfer From</h3>
                    <hr />
                    <div className="pt-form-row">
                        <input type="text"
                            name="pharmacyName"
                            placeholder="Pharmacy Name*"
                            required
                            onChange={handleChange}
                            value={formData.pharmacyName} />
                        <input type="text"
                            name="pharmacyPhone"
                            placeholder="Pharmacy Phone Number"
                            onChange={handleChange}
                            value={formData.pharmacyPhone} />
                    </div>

                    {/* PRESCRIPTIONS TO BE TRANSFERRED */}
                    <h3>Prescriptions to be Transferred</h3>
                    <hr />
                    <p>
                        If you choose to transfer only select prescriptions, please provide the drug name or prescription number for each one you'd like to transfer.
                    </p>
                    <div className="pt-radio-row">
                        <label><input type="radio"
                            name="transferType"
                            value="all"
                            checked={formData.transferType === 'all'}
                            onChange={handleChange} /> Transfer all my prescriptions</label>
                        {/* <label><input type="radio"
                            name="transferType"
                            value="selected"
                            checked={formData.transferType === 'selected'}
                            onChange={handleChange} /> Only transfer my selected prescriptions</label> */}
                    </div>

                    {/* PATIENT INFORMATION */}
                    <h3>Patient Information</h3>
                    <hr />
                    <div className="pt-form-row">
                        <input type="text"
                            name="firstName"
                            placeholder="First Name*"
                            required
                            onChange={handleChange}
                            value={formData.firstName} />
                        <input type="text"
                            name="lastName"
                            placeholder="Last Name*"
                            required
                            onChange={handleChange}
                            value={formData.lastName} />
                    </div>
                    <div className="pt-form-row">
                        <input type="email"
                            name="email"
                            placeholder="Email*"
                            required
                            onChange={handleChange}
                            value={formData.email} />
                        <input type="date"
                            name="birthDate"
                            placeholder="Birth Date*"
                            onFocus={(e) => (e.target.type = "date")}
                            onBlur={(e) => (e.target.type = "text")}
                            required
                            onChange={handleChange}
                            value={formData.birthDate} />
                    </div>
                    <input type="text"
                        name="phone"
                        placeholder="Phone*"
                        required
                        onChange={handleChange}
                        value={formData.phone} />

                    {/* CONSENT CHECKBOXES */}
                    <hr />

                    <div className="pt-checkbox">
                        <label><input type="checkbox"
                            name="consent1"
                            checked={formData.consent1}
                            onChange={handleChange}
                            required /> I consent to sending this information to the pharmacy selected above.</label>
                    </div>
                    <div className="pt-checkbox">
                        <label><input type="checkbox"
                            name="consent2"
                            checked={formData.consent2}
                            onChange={handleChange}
                            required /> I understand that some prescriptions cannot be transferred. In that case the pharmacist will contact you.</label>
                    </div>

                    <button type="submit">Request Transfer</button>
                </form>
            </div>
        </section>
        <Footer/>
    </>
    );
}

export default PrescriptionTransfer;